import { ref, onUnmounted, watch, computed, nextTick } from 'vue'
import modalTrapFocus from './modalTrapFocus'
import Loader from '../Loader/Loader.vue'
import NykButton from '../NykButton/NykButton.vue'

export default {
    name: 'Modal',
    components: {
        Loader,
        NykButton
    },
    props: {
        open: {
            type: Boolean
        },
        title: {
            type: String,
            default: ''
        },
        subtitle: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: 'medium',
            validator: (value) => ['small', 'medium', 'large', 'small-large'].includes(value)
        },
        theme: {
            type: String,
            default: 'white',
            validator: (value) => ['white', 'default', 'seamless'].includes(value)
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        closeAriaLabel: {
            type: String,
            default: 'Luk Modal'
        },
        notScrollable: {
            type: Boolean,
            default: false
        },
        bodyDisplay: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'flex'].includes(value)
        },
        bodyDirection: {
            type: String,
            default: 'none',
            validator: (value) => ['none', 'column', 'row'].includes(value)
        }
    },
    emits: ['close', 'open'],
    setup(props, { emit }) {
        const modalElementRef = ref(null)
        let keydownHandler = null
        let closeHandler = null
        let clickHandler = null

        const setupEventListeners = (modalElement) => {
            keydownHandler = (e) => modalTrapFocus(e, modalElement)
            document.addEventListener('keydown', keydownHandler)

            closeHandler = (event) => {
                if (event.target.returnValue === '') {
                    closeModal()
                }
            }
            modalElement.addEventListener('close', closeHandler)

            clickHandler = (event) => {
                if (event.target === modalElement) {
                    modalElement.close()
                }
            }
            modalElement.addEventListener('click', clickHandler)
        }

        const cleanupEventListeners = () => {
            if (keydownHandler) {
                document.removeEventListener('keydown', keydownHandler)
                keydownHandler = null
            }
            if (closeHandler && modalElementRef.value) {
                modalElementRef.value.removeEventListener('close', closeHandler)
                closeHandler = null
            }
            if (clickHandler && modalElementRef.value) {
                modalElementRef.value.removeEventListener('click', clickHandler)
                clickHandler = null
            }
        }

        // Watch for changes in the modal open state
        watch(() => props.open, async (value) => {
            if (value) {
                // Wait for DOM to render
                await nextTick()
                if (modalElementRef.value) {
                    const scrollPos = window.scrollY
                    modalElementRef.value.showModal()
                    setupEventListeners(modalElementRef.value)

                    // Restore the scroll position
                    setTimeout(() => {
                        window.scrollTo(0, scrollPos)
                    }, 50)
                    document.querySelector('body').classList.add('no-scroll')
                    emit('open')
                }
            } else {
                cleanupEventListeners()
                document.querySelector('body').classList.remove('no-scroll')
            }
        })

        const modalClasses = computed(() => ({
            [`nyk-modal--${props.size}`]: props.size !== 'medium',
            'nyk-modal--white': props.theme === 'white',
            'nyk-modal--seamless': props.theme === 'seamless',
            'nyk-modal--subtitle': !!props.subtitle,
            'nyk-modal--not-scrollable': props.notScrollable
        }))

        const contentClasses = computed(() => ({
            'nyk-modal__body__content--flex': props.bodyDisplay === 'flex',
            [`nyk-modal__body__content--${props.bodyDirection}`]: props.bodyDisplay === 'flex' && props.bodyDirection !== 'none'
        }))

        const closeButtonVariant = computed(() => props.theme === 'seamless' ? 'neutral' : 'default')

        const closeModal = () => {
            emit('close')
        }

        onUnmounted(() => {
            cleanupEventListeners()
        })

        return {
            modalElementRef,
            modalClasses,
            contentClasses,
            closeButtonVariant,
            closeModal
        }
    },
}
